import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  XCircle, MapPin, Dumbbell, Trash2, Search, ChevronRight, Eye, Loader2, RotateCcw
} from "lucide-react";

export function RejectedVerificationsTab({ rejectedVenues = [], onDeleteVenue, onViewVenue }) {
  const [rejectedSPs, setRejectedSPs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    const fetchRejectedSPs = async () => {
      try {
        const res = await api.get("/admin/kyc/all?persona=coach&status=rejected");
        setRejectedSPs(res.data.submissions || []);
      } catch {
        toast.error("Failed to load rejected SP data");
      }
      setLoading(false);
    };
    fetchRejectedSPs();
  }, []);

  const combinedList = useMemo(() => {
    const items = [];

    for (const v of rejectedVenues) {
      items.push({
        id: v.venue_id,
        type: "venue",
        name: v.name || "Unnamed Venue",
        owner: v.owner_name || "—",
        email: v.owner_email || "—",
        city: v.city || "—",
        rejected_at: v.verified_at || v.updated_at || v.created_at || "",
        raw: v,
      });
    }

    for (const sp of rejectedSPs) {
      const rejType = sp.credentials_review_status === "rejected" ? "Credentials" : "Identity";
      items.push({
        id: sp.user_id,
        type: "sp",
        name: sp.user_name || "Unknown",
        owner: sp.user_email || "—",
        email: sp.user_email || "—",
        city: `${rejType} rejected`,
        rejected_at: sp.credentials_reviewed_at || sp.identity_reviewed_at || sp.updated_at || "",
        raw: sp,
      });
    }

    return items
      .filter((item) => {
        if (typeFilter !== "all" && item.type !== typeFilter) return false;
        if (!query) return true;
        const text = [item.name, item.owner, item.email, item.city].join(" ").toLowerCase();
        return text.includes(query.toLowerCase());
      })
      .sort((a, b) => (b.rejected_at || "").localeCompare(a.rejected_at || ""));
  }, [rejectedVenues, rejectedSPs, query, typeFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="rejected-verifications-tab">
      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search rejected..."
            className="pl-9 h-9 text-sm"
            data-testid="rejected-search"
          />
        </div>
        <div className="flex gap-1.5">
          {[
            { value: "all", label: "All" },
            { value: "venue", label: "Venues" },
            { value: "sp", label: "Sports Professionals" },
          ].map((opt) => (
            <Button
              key={opt.value}
              variant={typeFilter === opt.value ? "default" : "outline"}
              size="sm"
              onClick={() => setTypeFilter(opt.value)}
              data-testid={`rejected-filter-${opt.value}`}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Count summary */}
      <div className="flex gap-2 text-xs">
        <Badge variant="outline" className="text-red-700 border-red-200">
          <XCircle className="w-3 h-3 mr-1" />{combinedList.length} rejected
        </Badge>
        <Badge variant="outline">{rejectedVenues.length} venues</Badge>
        <Badge variant="outline">{rejectedSPs.length} SPs</Badge>
      </div>

      {/* List */}
      {combinedList.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <XCircle className="w-10 h-10 mx-auto mb-2 opacity-20" />
            <p className="text-sm">No rejected verifications</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {combinedList.map((item) => (
            <Card key={`${item.type}-${item.id}`} className="hover:border-red-200 transition-colors" data-testid={`rejected-item-${item.type}-${item.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${item.type === "venue" ? "bg-red-50" : "bg-purple-50"}`}>
                      {item.type === "venue" ? <MapPin className="w-4 h-4 text-red-500" /> : <Dumbbell className="w-4 h-4 text-purple-600" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{item.name}</p>
                        <Badge className={`text-[9px] ${item.type === "venue" ? "bg-red-100 text-red-700" : "bg-purple-100 text-purple-700"}`}>
                          {item.type === "venue" ? "Venue" : "SP"}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{item.owner} · {item.city}</p>
                      {item.rejected_at && (
                        <p className="text-[10px] text-muted-foreground mt-0.5">Rejected: {item.rejected_at.slice(0, 10)}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {item.type === "venue" && onViewVenue && (
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onViewVenue(item.raw.venue_id)} data-testid={`rejected-view-${item.id}`}>
                        <Eye className="w-3 h-3 mr-1" />View
                      </Button>
                    )}
                    {item.type === "venue" && onDeleteVenue && (
                      <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => onDeleteVenue(item.raw.venue_id)} data-testid={`rejected-delete-${item.id}`}>
                        <Trash2 className="w-3 h-3 mr-1" />Delete
                      </Button>
                    )}
                    {item.type === "sp" && (
                      <Badge variant="outline" className="text-[10px]">
                        <RotateCcw className="w-3 h-3 mr-1" />Awaiting resubmission
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
